import APIService from "./APIService";

/**
 * API service for Shield Reports operations
 * Handles report generation, retrieval, and Shield Score data
 */
class ReportingAPI {
  // ==================== REPORT GENERATION ====================

  /**
   * Generate a Personal Shield Report
   * @param {Object} options - Generation options
   * @param {string} options.periodType - "WEEKLY" or "MONTHLY"
   * @param {string} [options.periodStart] - Optional ISO date string for period start
   * @param {boolean} [options.sendEmail] - Whether to send email notification
   * @returns {Promise<Object>} Generation result with reportUuid
   */
  async generatePersonalReport(options = {}) {
    try {
      const requestBody = {
        periodType: options.periodType || "WEEKLY",
        sendEmail: options.sendEmail || false,
      };

      // Only include periodStartDate if explicitly provided
      if (options.periodStart) {
        requestBody.periodStartDate = options.periodStart;
      }

      const response = await APIService.post(
        "/api/v1/app/reporting/personal/generate",
        requestBody,
      );
      return response;
    } catch (error) {
      console.error("Error generating report:", error);
      throw error;
    }
  }

  // ==================== REPORT RETRIEVAL ====================

  /**
   * Get a specific report by UUID
   * @param {string} reportUuid - Report UUID
   * @returns {Promise<Object>} Full report data
   */
  async getReport(reportUuid) {
    try {
      const response = await APIService.get(
        `/api/v1/app/reporting/${reportUuid}`,
      );
      return response;
    } catch (error) {
      console.error("Error getting report:", error);
      throw error;
    }
  }

  /**
   * Get list of reports for current user
   * @param {Object} options - Pagination options
   * @param {number} [options.page=0] - Page number (0-indexed)
   * @param {number} [options.size=10] - Page size
   * @param {string} [options.reportType] - Filter by report type
   * @returns {Promise<Object>} Paginated report list
   */
  async listReports(options = {}) {
    try {
      const params = new URLSearchParams();
      params.append("page", options.page || 0);
      params.append("size", options.size || 10);
      if (options.reportType) {
        params.append("reportType", options.reportType);
      }

      const response = await APIService.get(
        `/api/v1/app/reporting/list?${params.toString()}`,
      );
      return response;
    } catch (error) {
      console.error("Error listing reports:", error);
      throw error;
    }
  }

  /**
   * Get the most recent report
   * @returns {Promise<Object>} Latest report data
   */
  async getLatestReport() {
    try {
      const response = await APIService.get("/api/v1/app/reporting/latest");
      return response;
    } catch (error) {
      console.error("Error getting latest report:", error);
      throw error;
    }
  }

  // ==================== SHIELD SCORE ====================

  /**
   * Get current Shield Score for dashboard display
   * @returns {Promise<Object>} Current score summary
   */
  async getCurrentShieldScore() {
    try {
      const response = await APIService.get(
        "/api/v1/app/reporting/score/current",
      );
      return response;
    } catch (error) {
      console.error("Error getting shield score:", error);
      throw error;
    }
  }

  /**
   * Get Shield Score history for trend charts
   * @param {Object} options - History options
   * @param {string} [options.periodType="WEEKLY"] - Period type
   * @param {number} [options.limit=12] - Number of historical periods
   * @returns {Promise<Array>} Array of historical scores
   */
  async getScoreHistory(options = {}) {
    try {
      const params = new URLSearchParams();
      params.append("periodType", options.periodType || "WEEKLY");
      params.append("limit", options.limit || 12);

      const response = await APIService.get(
        `/api/v1/app/reporting/score/history?${params.toString()}`,
      );
      return response;
    } catch (error) {
      console.error("Error getting score history:", error);
      throw error;
    }
  }

  // ==================== PDF DOWNLOAD ====================

  /**
   * Download report as PDF
   * @param {string} reportUuid - Report UUID
   * @returns {Promise<Blob>} PDF blob for download
   */
  async downloadReportPdf(reportUuid) {
    try {
      const response = await APIService.getBlob(
        `/api/v1/app/reporting/${reportUuid}/pdf`,
      );
      return response;
    } catch (error) {
      console.error("Error downloading PDF:", error);
      throw error;
    }
  }

  /**
   * Helper to trigger PDF download in browser
   * @param {string} reportUuid - Report UUID
   * @param {string} [filename] - Optional filename
   */
  async downloadPdfToDevice(reportUuid, filename) {
    try {
      const blob = await this.downloadReportPdf(reportUuid);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        filename || `kinshield-report-${reportUuid.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF to device:", error);
      throw error;
    }
  }

  // ==================== EMAIL OPERATIONS ====================

  /**
   * Resend report email
   * @param {string} reportUuid - Report UUID
   * @param {boolean} [includePdf=false] - Whether to attach PDF
   * @returns {Promise<Object>} Send result
   */
  async resendReportEmail(reportUuid, includePdf = false) {
    try {
      const response = await APIService.post(
        `/api/v1/app/reporting/${reportUuid}/email?includePdf=${includePdf}`,
      );
      return response;
    } catch (error) {
      console.error("Error resending email:", error);
      throw error;
    }
  }

  // ==================== ELIGIBILITY ====================

  /**
   * Check if user is eligible for report generation
   * @returns {Promise<Object>} Eligibility status
   */
  async checkEligibility() {
    try {
      const response = await APIService.get(
        "/api/v1/app/reporting/eligibility",
      );
      return response;
    } catch (error) {
      console.error("Error checking eligibility:", error);
      throw error;
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Format score level for display
   * @param {string} level - Score level from API
   * @returns {string} Formatted display text
   */
  formatScoreLevel(level) {
    const labels = {
      EXCELLENT: "Excellent",
      GOOD: "Good",
      FAIR: "Building Up 🌱",
      NEEDS_IMPROVEMENT: "Good Start — Keep Going!",
      AT_RISK: "Just Getting Started",
    };
    return labels[level] || level;
  }

  /**
   * Get color for score level
   * @param {number} score - Shield score
   * @returns {string} CSS color value
   */
  getScoreColor(score) {
    if (score >= 90) return "#10B981"; // Green
    if (score >= 75) return "#3B82F6"; // Blue
    if (score >= 50) return "#F59E0B"; // Yellow/Orange
    if (score >= 25) return "#EF4444"; // Red
    return "#DC2626"; // Dark Red
  }

  /**
   * Get score level from numeric score
   * @param {number} score - Shield score
   * @returns {string} Score level
   */
  getScoreLevel(score) {
    if (score >= 90) return "EXCELLENT";
    if (score >= 75) return "GOOD";
    if (score >= 50) return "FAIR";
    if (score >= 25) return "NEEDS_IMPROVEMENT";
    return "AT_RISK";
  }

  /**
   * Format period label for display
   * @param {string} periodType - WEEKLY or MONTHLY
   * @param {string} periodStart - ISO date string
   * @param {string} periodEnd - ISO date string
   * @returns {string} Formatted period label
   */
  formatPeriodLabel(periodType, periodStart, periodEnd) {
    const start = new Date(periodStart);
    const end = new Date(periodEnd);

    if (periodType === "WEEKLY") {
      const startStr = start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const endStr = end.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `Week of ${startStr} - ${endStr}`;
    } else {
      return start.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
  }

  /**
   * Format trend indicator
   * @param {string} direction - UP, DOWN, STABLE, NEW
   * @param {number} percentage - Trend percentage
   * @returns {Object} Trend display data
   */
  formatTrend(direction, percentage) {
    const icons = {
      UP: "↑",
      DOWN: "↓",
      STABLE: "→",
      NEW: "★",
    };

    const colors = {
      UP: "#10B981",
      DOWN: "#EF4444",
      STABLE: "#6B7280",
      NEW: "#8B5CF6",
    };

    return {
      icon: icons[direction] || "",
      color: colors[direction] || "#6B7280",
      text:
        direction === "NEW"
          ? "First Report!"
          : `${Math.abs(percentage).toFixed(1)}% ${direction.toLowerCase()}`,
    };
  }
}

// Export singleton instance
const reportingAPI = new ReportingAPI();
export default reportingAPI;
